import * as React from "react"
import * as Popover from "@radix-ui/react-popover"
import { DayPicker } from "react-day-picker"
import { format } from "date-fns"
import { CalendarIcon } from "@heroicons/react/24/outline"
import { cn } from "../../lib/utils"
import "react-day-picker/dist/style.css"

interface DatePickerProps {
  date?: Date
  onSelect?: (date?: Date) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  defaultMonth?: Date
}

export function DatePicker({
  date,
  onSelect,
  disabled = false,
  placeholder = "Select date",
  className,
  defaultMonth,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left text-sm",
            "hover:bg-muted transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !date && "text-muted-foreground",
            className
          )}
        >
          <span>{date ? format(date, "PPP") : placeholder}</span>
          <CalendarIcon className="ml-2 h-4 w-4 text-muted-foreground" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="z-50 rounded-lg border border-border bg-card p-4 shadow-lg"
          sideOffset={5}
          align="start"
        >
          <DayPicker
            mode="single"
            selected={date}
            defaultMonth={defaultMonth || date}
            onSelect={(selectedDate) => {
              onSelect?.(selectedDate)
              setOpen(false) // Close popover after selection
            }}
            disabled={disabled}
            classNames={{
              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              month: "space-y-4",
              caption: "flex justify-center pt-1 relative items-center mb-2",
              caption_label: "text-sm font-medium text-foreground",
              nav: "absolute top-0 left-0 right-0 flex justify-between items-center h-10",
              nav_button: cn(
                "h-8 w-8 bg-transparent p-0 rounded-md transition-colors",
                "text-muted-foreground hover:text-foreground hover:bg-muted",
                "inline-flex items-center justify-center"
              ),
              nav_button_previous: "",
              nav_button_next: "",
              table: "w-full border-collapse",
              head_row: "flex",
              head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
              row: "flex w-full mt-1",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
              day: cn(
                "h-9 w-9 p-0 font-normal rounded-md",
                "hover:bg-muted hover:text-foreground transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary"
              ),
              day_selected:
                "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground font-semibold",
              day_today: "font-semibold", // Very subtle - just bold text, no background
              day_outside: "text-muted-foreground opacity-50",
              day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
              day_hidden: "invisible",
            }}
            showOutsideDays
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
